import React from 'react';
import { useTranslation } from 'react-i18next';

interface GroupByChipsProps {
  value: string[];
  onChange: (next: string[]) => void;
  available: string[];
  /** When the "date" chip is active and granularity control is needed,
   *  pass these props to show an inline granularity selector on the Date chip. */
  granularity?: 'daily' | 'weekly' | 'monthly';
  onGranularityChange?: (v: 'daily' | 'weekly' | 'monthly') => void;
}

// Multi-select chip group for dimension selection.
// The "date" chip optionally includes an inline granularity dropdown
// so the user controls both "group by date" and "at what granularity"
// in a single, intuitive control.
export const GroupByChips: React.FC<GroupByChipsProps> = ({
  value, onChange, available, granularity, onGranularityChange,
}) => {
  const { t } = useTranslation();

  const labels: Record<string, string> = {
    site:                t('financialReport.groupBySite',         { defaultValue: 'Site' }),
    date:                t('financialReport.groupByDate',         { defaultValue: 'Date' }),
    payment_method_type: t('financialReport.groupByPaymentType',  { defaultValue: 'Payment Type' }),
    payment_method:      t('financialReport.groupByPaymentMethod',{ defaultValue: 'Payment Method' }),
    currency:            t('financialReport.groupByCurrency',     { defaultValue: 'Currency' }),
    game:                t('gamePerformance.groupByGame',          { defaultValue: 'Game' }),
    brand:               t('gamePerformance.groupByBrand',        { defaultValue: 'Brand' }),
    game_type:           t('gamePerformance.groupByGameType',     { defaultValue: 'Game Type' }),
  };

  const granularityLabels: Record<string, string> = {
    daily:   t('filters.daily',   { defaultValue: 'Daily' }),
    weekly:  t('filters.weekly',  { defaultValue: 'Weekly' }),
    monthly: t('filters.monthly', { defaultValue: 'Monthly' }),
  };

  const toggle = (dim: string) => {
    if (value.includes(dim)) onChange(value.filter((d) => d !== dim));
    else onChange([...value, dim]);
  };

  const dateActive = value.includes('date');
  const showGranSelector = dateActive && granularity && onGranularityChange;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
        {t('financialReport.groupByLabel', { defaultValue: 'Group by:' })}
      </span>
      {available.map((dim) => {
        const active = value.includes(dim);

        // Date chip with inline granularity selector
        if (dim === 'date' && showGranSelector) {
          return (
            <span key={dim} className="inline-flex items-center gap-0 rounded-full bg-accent text-white">
              <button
                type="button"
                onClick={() => toggle(dim)}
                className="rounded-l-full px-3 py-1 text-xs font-medium hover:bg-accent/80 transition-colors"
              >
                {labels[dim]}
              </button>
              <select
                value={granularity}
                onChange={(e) => onGranularityChange!(e.target.value as 'daily' | 'weekly' | 'monthly')}
                onClick={(e) => e.stopPropagation()}
                className="appearance-none rounded-r-full border-l border-white/30 bg-transparent py-1 pl-1 pr-5 text-xs font-medium text-white outline-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
              >
                <option value="daily" className="text-slate-900">{granularityLabels.daily}</option>
                <option value="weekly" className="text-slate-900">{granularityLabels.weekly}</option>
                <option value="monthly" className="text-slate-900">{granularityLabels.monthly}</option>
              </select>
            </span>
          );
        }

        return (
          <button
            key={dim}
            type="button"
            onClick={() => toggle(dim)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              active
                ? 'bg-accent text-white'
                : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            {labels[dim] ?? dim}
          </button>
        );
      })}
    </div>
  );
};
